/**
 * How a list of chat sessions reads, wherever it is shown.
 *
 * Ask lists conversations in three places — the page's history column, the
 * panel's history menu, and the agent rail — and they are the same list, so
 * they group and date the same way rather than each inventing its own.
 */

/** "Just now", a time today, a date before that. */
export function formatSessionTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMs = new Date() - d;
    if (diffMs < 60_000) return 'Just now';
    if (diffMs < 86_400_000) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/** "now", "5m", "3h", "2d", "3w": the age a history row shows, as the rail does. */
export function formatSessionAge(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (secs < 60) return 'now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  if (secs < 7 * 86400) return `${Math.floor(secs / 86400)}d`;
  return `${Math.floor(secs / (7 * 86400))}w`;
}

const BUCKETS = ['Today', 'Yesterday', 'Previous 7 days', 'Older'];

/**
 * Split sessions into date buckets, newest bucket first, dropping the empty
 * ones. Input order is preserved inside each bucket (the API returns them
 * newest first), so this only groups — it does not re-sort.
 *
 * @param {Array<{updatedAt?: string}>} sessions
 * @returns {Array<{label: string, items: Array}>}
 */
export function groupSessionsByRecency(sessions) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const buckets = { Today: [], Yesterday: [], 'Previous 7 days': [], Older: [] };
  for (const s of sessions || []) {
    const raw = s.updatedAt || s.updated_at || s.createdAt || s.created_at;
    const d = raw ? new Date(raw) : null;
    if (!d || Number.isNaN(d.getTime())) { buckets.Older.push(s); continue; }
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (day >= today) buckets.Today.push(s);
    else if (day >= yesterday) buckets.Yesterday.push(s);
    else if (day >= weekAgo) buckets['Previous 7 days'].push(s);
    else buckets.Older.push(s);
  }
  return BUCKETS.filter((label) => buckets[label].length > 0).map((label) => ({ label, items: buckets[label] }));
}

/** The label above a bucket of conversations, in every list that shows one. */
export const SESSION_GROUP_LABEL_CLASS =
  'px-2 pb-1 pt-3 text-[11px] font-medium text-[color:var(--text-muted)]';

/**
 * The element a page portals a full-height side panel into. Dashboard renders
 * it as a flex sibling of the content column, next to where Ask stands, so a
 * panel placed there starts at the top of the content panel rather than under
 * the page's path bar — and still shrinks the page rather than covering it.
 */
export const SIDE_PANEL_SLOT_ID = 'trodo-side-panel-slot';
